import type { BoxProps } from '@chakra-ui/react';
import { Box } from '@chakra-ui/react';
import { useCallback, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import './index.scss';

interface RippleEffectProps extends BoxProps {
  children: ReactNode;
  enabled?: boolean;
  rippleColor?: string;
  duration?: number;
}

interface Ripple {
  id: number;
  x: number;
  y: number;
  size: number;
}

export function RippleEffect({
  children,
  enabled = true,
  rippleColor = 'rgba(0, 0, 0, 0.1)',
  duration = 600,
  ...props
}: RippleEffectProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [ripples, setRipples] = useState<Ripple[]>([]);
  const rippleIdRef = useRef(0);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const container = containerRef.current;
      if (!container || !enabled) return;

      const rect = container.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      const size = Math.max(rect.width, rect.height);

      const newRipple: Ripple = {
        id: rippleIdRef.current++,
        x,
        y,
        size,
      };

      setRipples((prev) => [...prev, newRipple]);

      const timeout = setTimeout(() => {
        setRipples((prev) => prev.filter((ripple) => ripple.id !== newRipple.id));
      }, duration);

      return () => {
        clearTimeout(timeout);
      };
    },
    [duration, enabled]
  );

  return (
    <Box
      className="RippleEffect"
      ref={containerRef}
      position="relative"
      overflow="hidden"
      minHeight="fit-content"
      onClick={handleClick}
      onContextMenu={handleClick}
      {...props}
    >
      {children}
      {ripples.map((ripple) => (
        <Box
          key={ripple.id}
          position="absolute"
          borderRadius="50%"
          bg={rippleColor}
          left={`${ripple.x}px`}
          top={`${ripple.y}px`}
          width={`${ripple.size * 2}px`}
          height={`${ripple.size * 2}px`}
          transform="translate(-50%, -50%) scale(0)"
          pointerEvents="none"
          style={{
            animation: `ripple-animation ${duration}ms ease-out`,
          }}
        />
      ))}
    </Box>
  );
}
